import React, { useState, useEffect } from "react";
import { ChevronRight, Lock, WifiOff } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area } from "recharts";

export const fmt = (n) => (n ?? 0).toLocaleString("de-CH");

// Einheitlicher ehrlicher Leerzustand, solange die Website-Analyse nicht verbunden ist (ECHT-Regel).
export const AnalyticsOffline = ({ t, title, hint, minH = 140, testid = "analytics-offline", action = null }) => (
  <div className="flex flex-col items-center justify-center text-center px-4 py-6" style={{ minHeight: minH }} data-testid={testid}>
    <span className="w-11 h-11 rounded-2xl flex items-center justify-center bg-white/5 border border-white/10 mb-3">
      <WifiOff className="w-5 h-5 text-gray-500" />
    </span>
    <div className="text-sm font-semibold text-gray-300">{title || t("Noch nicht verbunden")}</div>
    <p className="text-xs text-gray-500 mt-1 max-w-[300px]">
      {hint || t("Echte Zahlen erscheinen, sobald Ihre Website-Analyse aktiv ist.")}
    </p>
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Title = ({ title, subtitle }) => (
  <div className="mb-5">
    <h1 className="text-[28px] leading-tight font-black tracking-tight">{title}</h1>
    {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
  </div>
);

export const Card = ({ children, className = "", ...p }) => (
  <div className={`bg-[#121217] border border-white/10 rounded-2xl ${className}`} {...p}>{children}</div>
);

export const StatTile = ({ icon: Icon, value, label, sub, accent, testid, onClick }) => (
  <button onClick={onClick} data-testid={testid}
    className="text-left bg-[#121217] border border-white/10 rounded-2xl p-4 hover:border-white/20 transition-colors w-full">
    <div className="flex items-center justify-between">
      <span className="text-3xl font-black tracking-tight">{value}</span>
      {Icon && <Icon className="w-5 h-5" style={{ color: accent }} />}
    </div>
    <div className="text-sm font-semibold mt-1">{label}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </button>
);

export const BtnPrimary = ({ children, accent, className = "", ...p }) => (
  <button {...p} style={{ background: accent }}
    className={`text-black font-bold rounded-xl px-5 py-3 flex items-center justify-center gap-2 transition-transform active:scale-95 hover:brightness-105 ${className}`}>
    {children}
  </button>
);

export const BtnSecondary = ({ children, className = "", ...p }) => (
  <button {...p}
    className={`bg-white/5 text-white font-semibold rounded-xl px-5 py-3 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-center gap-2 ${className}`}>
    {children}
  </button>
);

export const RowLink = ({ icon: Icon, dot, title, meta, badge, onClick, accent, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="w-full flex items-center gap-3 py-3 border-b border-white/5 last:border-0 text-left hover:opacity-90">
    {Icon && <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${accent}1A` }}><Icon className="w-4.5 h-4.5" style={{ color: accent }} /></div>}
    {dot && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dot }} />}
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-sm truncate">{title}</div>
      {meta && <div className="text-xs text-gray-500 truncate">{meta}</div>}
    </div>
    {badge}
    <ChevronRight className="w-4 h-4 text-gray-600 shrink-0" />
  </button>
);

export const Pill = ({ active, children, onClick, accent, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${active ? "text-black border-transparent" : "text-gray-400 border-white/10 hover:text-white"}`}
    style={active ? { background: accent } : {}}>
    {children}
  </button>
);

const TONE = {
  green: (a) => ({ background: `${a}22`, color: a, borderColor: `${a}55` }),
  amber: () => ({ background: "rgba(251,191,36,0.15)", color: "#fcd34d", borderColor: "rgba(251,191,36,0.3)" }),
  blue: () => ({ background: "rgba(59,130,246,0.15)", color: "#93c5fd", borderColor: "rgba(59,130,246,0.3)" }),
  red: () => ({ background: "rgba(239,68,68,0.15)", color: "#fca5a5", borderColor: "rgba(239,68,68,0.3)" }),
  slate: () => ({ background: "rgba(255,255,255,0.08)", color: "#d1d5db", borderColor: "rgba(255,255,255,0.12)" }),
};
export const Badge = ({ tone = "slate", accent = "#A3E635", children }) => (
  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border" style={TONE[tone](accent)}>{children}</span>
);

export const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 mb-3">{children}</div>
);

const ICON_TONES = {
  lime: "#A3E635", green: "#22C55E", amber: "#F59E0B", blue: "#3B82F6", purple: "#8B5CF6", red: "#EF4444",
};

// Premium-KPI-Kachel: einheitliche Höhe, farbiges Icon, grosser Wert, Hover-Akzentlinie.
export const KpiTile = ({ icon: Icon, value, label, sub, tone = "lime", accent, trend, onClick, testid, primary }) => {
  const c = tone === "lime" ? (accent || ICON_TONES.lime) : (ICON_TONES[tone] || accent || ICON_TONES.lime);
  return (
    <button onClick={onClick} data-testid={testid}
      className={`group text-left rounded-2xl p-4 lg:p-5 hover:-translate-y-0.5 transition-all w-full h-full min-h-[122px] flex flex-col justify-between relative overflow-hidden border ${primary ? "bg-[#171b14]" : "bg-[#15181e] border-white/5 hover:border-white/15"}`}
      style={primary ? { borderColor: `${c}66`, boxShadow: `inset 0 0 0 1px ${c}22` } : {}}>
      <span className="absolute top-0 inset-x-0 h-[2px] transition-opacity duration-300" style={{ background: c, opacity: primary ? 0.9 : 0 }} />
      <span className="absolute top-0 inset-x-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: c }} />
      <div className="flex items-center justify-between">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c}1F`, color: c }}>
          {Icon && <Icon className="w-5 h-5" />}
        </span>
        {trend != null && trend !== "" && (
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-md" style={{ color: ICON_TONES.green, background: `${ICON_TONES.green}1A` }}>{trend}</span>
        )}
      </div>
      <div>
        <div className={`${typeof value === "string" && value.length > 10 ? "text-[20px] lg:text-[16px] xl:text-[17px] 2xl:text-[22px] leading-tight" : "text-[26px] lg:text-[30px] leading-none"} font-black tracking-tight mt-3 [overflow-wrap:anywhere]`}>{value}</div>
        <div className="text-[13px] font-semibold text-gray-200 mt-1.5 truncate">{label}</div>
        {sub && <div className="text-[11px] text-gray-500 mt-0.5 truncate">{sub}</div>}
      </div>
    </button>
  );
};

// Leichtgewichtige Sparkline (recharts Area) fuer Monatsreport-Mini-Charts.
export const Sparkline = ({ data = [], color = "#A3E635", height = 38 }) => {
  const pts = (data && data.length ? data : [0, 0]).map((v, i) => ({ i, v: Number(v) || 0 }));
  const id = React.useMemo(() => `spark-${Math.random().toString(36).slice(2, 8)}`, []);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={pts} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${id})`} isAnimationActive={true} animationDuration={600} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Kreisfoermiger Health-Score (SVG-Ring). Animierter Count-up beim Laden. Zahl & Label-Farbe parametrierbar.
export const HealthRing = ({ score = 0, size = 68, stroke = 6, color = "#A3E635", label, valueColor, labelColor }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, score));
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 1100;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(pct * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(pct);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pct]);
  const off = circ - (val / 100) * circ;
  const numSize = Math.max(16, Math.round(size * 0.3));
  const labSize = Math.max(8, Math.round(size * 0.085));
  return (
    <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }} data-testid="ws-health-ring">
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={off} />
      </svg>
      <div className="absolute flex flex-col items-center leading-none">
        <span className="font-black tabular-nums" style={{ color: valueColor || color, fontSize: numSize }}>{Math.round(val)}</span>
        {label && <span className="uppercase tracking-wider mt-1 font-bold" style={{ color: labelColor || "#6b7280", fontSize: labSize }}>{label}</span>}
      </div>
    </div>
  );
};


// Echtes Website-Vorschaubild via WordPress mShots (kostenlos, kein Key); NIE beschnitten (16:10, object-contain).
// Alle 6 Stunden frisches Bild (Cache-Bust). Fallback auf Marken-Mock.
export const WebsitePreview = ({ url, domain, initial, accent, t, live, className = "", maxWidthClass = "max-w-[620px]" }) => {
  const [err, setErr] = useState(false);
  const refreshBucket = Math.floor(Date.now() / (6 * 60 * 60 * 1000));
  const src = url ? `https://s.wordpress.com/mshots/v1/${encodeURIComponent(url)}?w=1280&h=800&cb=${refreshBucket}` : null;
  return (
    <div className={`w-full ${maxWidthClass} mx-auto rounded-2xl border border-white/10 overflow-hidden bg-[#0d0f14] shadow-xl shadow-black/40 ${className}`} data-testid="ws-preview">
      <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-white/10 bg-white/[0.04]">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" /><span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" /><span className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
        <div className="ml-2 flex-1 text-[11px] text-gray-400 bg-black/40 rounded-md px-2.5 py-1 truncate flex items-center gap-1.5"><Lock className="w-3 h-3" style={{ color: accent }} /> {domain || (t ? t("noch keine Domain") : "")}</div>
      </div>
      <div className="relative aspect-[16/10] bg-[#0d0f14]">
        {src && !err && (
          <img src={src} alt={domain} loading="lazy" onError={() => setErr(true)} className="absolute inset-0 w-full h-full object-contain object-top" />
        )}
        {(!src || err) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2" style={{ background: `radial-gradient(120% 100% at 50% 0%, ${accent}1A 0%, #0d0f14 60%)` }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl font-black" style={{ background: `${accent}22`, color: accent }}>{initial}</div>
            <div className="text-sm font-semibold text-gray-200">{domain || (t ? t("Website in Vorbereitung") : "")}</div>
          </div>
        )}
        {live && <span className="absolute bottom-2 left-2 z-10 flex items-center gap-1.5 text-[11px] font-semibold px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm" style={{ color: accent }}><span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }} /> {t ? t("Live & erreichbar") : ""}</span>}
      </div>
    </div>
  );
};


// Website-Gesundheits-Score (geteilt: Overview + CWebsite)
export function healthScore(states) {
  if (!states || typeof states !== "object") return 0;
  const W = { reachability: 45, ssl: 30, wordpress: 15, backup: 5, forms: 3, analytics: 2 };
  const val = (c) => (c === "green" ? 1 : c === "amber" || c === "blue" ? 0.6 : c === "red" ? 0 : null);
  let num = 0, den = 0;
  for (const k in W) {
    const v = val((states[k] || {}).color);
    if (v === null) continue;
    num += W[k] * v;
    den += W[k];
  }
  return den === 0 ? 0 : Math.round((num / den) * 100);
}
export const healthColor = (s) => (s >= 85 ? "#A3E635" : s >= 60 ? "#fbbf24" : "#f87171");
export const healthBand = (s, t) => (s >= 90 ? t("Sehr gut") : s >= 75 ? t("Gut") : s >= 50 ? t("Mittel") : t("Mangelhaft"));
